// Putting a lesson night on Meal Hat's own schedule.
//
// Matt: "have them assigned to days in the regular meal hat rotation.
// Although they don't go into the hat, they just get assigned." That is
// exactly Meal Hat's ONE-OFF shape (store/index.js scheduleMeal): a
// drawnMeals row with a name and `manual: true` and NO mealId, so nothing is
// added to the hat and nothing is drawn later. Meal Hat's schedule, its
// calendar and the Magic Mirror feed all render the row by its name.
//
// The row CARRIES ITS INGREDIENTS, in a Meal Hat meal's own shape
// (`[{ groceryItemId, quantity }]`), and Meal Hat's shopping-list rebuild
// reads them like any drawn meal's (Matt, 2026-10-02: "that should
// definitely just happen automatically, just like whenever any other meal
// gets scheduled" — it used to be a separate "Add to the list" button). This
// app can't run that rebuild, so every write here raises
// `<hat>/schema/shoppingListStale` and the open (or next-opened) Meal Hat
// rebuilds. Only the lesson's non-pantry groceries go on the row — Matt's own
// meals list the distinctive things, never salt or oil — and the creamery
// order is bought separately.
//
// `lesson` on the row is this app's own marker so the row can be found and
// moved or removed when the plan changes. Meal Hat ignores it.
//
// Meal Hat republishes the Magic Mirror feed on its next open or draw. A
// draw in Meal Hat does not avoid an assigned night — pick the draw's date
// range around the lessons.
import { dbGet, dbPut, dbPatch, dbDelete } from '../store/db.js';
import { LESSONS } from '../curriculum/lessons.js';
import { catalogEntryFor } from './inHouse.js';
import { todayISO } from './course.js';

const uuid = () => (globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

/** Meal Hat's own id shape for a catalog entry it creates in-app. */
const newGroceryId = () => `${Date.now()}-${uuid()}`;

// Just the meal, short. It used to be "Lesson 1: Pork chops with a mustard
// pan sauce", which Meal Hat's schedule, calendar and the mirror all had to
// squeeze in (Matt, 2026-09-28: "It doesn't need to say lesson one and all
// that stuff").
export const SHORT_NAME_MAX = 22;
export const scheduleName = (lesson) => (lesson.short || lesson.dish).slice(0, SHORT_NAME_MAX);

/**
 * Rows this app already wrote keep whatever name they were written with, so
 * a name change here reaches them only through this: a patch renaming every
 * lesson row whose name is not what `scheduleName` says now. Only rows that
 * exist and carry our `lesson` marker — patching a row id alone would create
 * a stub row if Meal Hat had deleted it.
 */
export function renamePatch (drawnMeals = {}, lessons = []) {
  const patch = {};
  for (const [id, row] of Object.entries(drawnMeals || {})) {
    const lesson = row?.lesson && lessons.find((l) => l.id === row.lesson);
    if (!lesson) continue;
    const name = scheduleName(lesson);
    if (row.name !== name) patch[`drawnMeals/${id}/name`] = name;
  }
  return patch;
}

/**
 * What goes on a lesson's row: its non-pantry groceries as Meal Hat
 * ingredients, matched to the catalog by name or alias. A grocery the catalog
 * has never seen gets an entry in Meal Hat's own shape (returned in
 * `created`), because the rebuild skips ids it cannot find. Pure.
 */
export function lessonIngredients (lesson, catalog = {}) {
  const created = {};
  const ingredients = [];
  for (const item of lesson.grocery || []) {
    if (item.pantry) continue;
    let entry = catalogEntryFor(item, { ...catalog, ...created });
    if (!entry) {
      const id = newGroceryId();
      entry = { id, name: item.name, defaultUnits: '', defaultAisle: 0, defaultLocation: null };
      created[id] = entry;
    }
    ingredients.push({ groceryItemId: entry.id, quantity: parseInt(item.qty, 10) || 1 });
  }
  return { ingredients, created };
}

async function ingredientsFor (hat, lesson) {
  const catalog = (await dbGet(`${hat}/grocery-catalog`)) || {};
  const { ingredients, created } = lessonIngredients(lesson, catalog);
  for (const [id, entry] of Object.entries(created)) await dbPut(`${hat}/grocery-catalog/${id}`, entry);
  return ingredients;
}

/** Asks Meal Hat to rebuild its shopping list (see the header). */
export async function markListStale (hat) {
  await dbPut(`${hat}/schema/shoppingListStale`, Date.now());
}

const rowFor = (id, lesson, isoDate, ingredients) =>
  ({ id, name: scheduleName(lesson), assignedDate: isoDate, manual: true, lesson: lesson.id, ingredients });

/** Writes the one-off row; returns its id so the course entry can remember it. */
export async function placeOnSchedule (hat, lesson, isoDate) {
  const id = `${Date.now()}-${uuid()}`;
  await dbPut(`${hat}/drawnMeals/${id}`, rowFor(id, lesson, isoDate, await ingredientsFor(hat, lesson)));
  await markListStale(hat);
  return id;
}

export async function moveOnSchedule (hat, lesson, rowId, isoDate) {
  if (!rowId) return placeOnSchedule(hat, lesson, isoDate);
  await dbPut(`${hat}/drawnMeals/${rowId}`, rowFor(rowId, lesson, isoDate, await ingredientsFor(hat, lesson)));
  await markListStale(hat);
  return rowId;
}

export async function removeFromSchedule (hat, rowId) {
  if (!rowId) return;
  await dbDelete(`${hat}/drawnMeals/${rowId}`);
  await markListStale(hat);
}

/** 'YYYY-MM-DD' a fortnight back, local time: how far back lesson rows are read. */
const fortnightAgoISO = (now = new Date()) => todayISO(new Date(now.getTime() - 14 * 86400000));

/**
 * Which night each lesson is on, read off Meal Hat's schedule itself: lesson
 * id → 'YYYY-MM-DD', found by the `lesson` marker on the row. Pure.
 *
 * The schedule is the truth, not the course's own `plannedFor` note (Matt,
 * 2026-10-02: tonight's lesson was on Meal Hat's schedule and its page said
 * "Not on a night yet"). The note is written only by the week page, so a
 * night placed or moved anywhere else never reached it. A lesson on more
 * than one row shows its next night, or failing that its latest one.
 */
export function lessonNights (drawnMeals = {}, today = '') {
  const nights = {};
  for (const row of Object.values(drawnMeals || {})) {
    const iso = typeof row?.assignedDate === 'string' ? row.assignedDate.slice(0, 10) : '';
    if (!row?.lesson || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) continue;
    const held = nights[row.lesson];
    const better = !held ||
      (iso >= today && (held < today || iso < held)) ||
      (iso < today && held < today && iso > held);
    if (better) nights[row.lesson] = iso;
  }
  return nights;
}

/** lessonNights from the live schedule, or null when it could not be read. */
export async function loadLessonNights (hat, now = new Date()) {
  if (!hat) return null;
  try {
    const drawn = await dbGet(`${hat}/drawnMeals`, `orderBy="assignedDate"&startAt="${fortnightAgoISO(now)}"`);
    return lessonNights(drawn, todayISO(now));
  } catch {
    return null;
  }
}

/**
 * Brings lesson rows from a fortnight back onward up to date: names (see
 * renamePatch) and, for rows written before rows carried ingredients, the
 * ingredients — then asks Meal Hat for a rebuild. Quiet: a failure changes
 * nothing, and the next load tries again.
 */
export async function refreshScheduleRows (hat) {
  if (!hat) return;
  try {
    const drawn = await dbGet(`${hat}/drawnMeals`, `orderBy="assignedDate"&startAt="${fortnightAgoISO()}"`);
    const patch = renamePatch(drawn, LESSONS);
    let missing = false;
    for (const [id, row] of Object.entries(drawn || {})) {
      const lesson = row?.lesson && !row.ingredients && LESSONS.find((l) => l.id === row.lesson);
      if (!lesson) continue;
      const ingredients = await ingredientsFor(hat, lesson);
      ingredients.forEach((ingredient, i) => { patch[`drawnMeals/${id}/ingredients/${i}`] = ingredient; });
      missing = true;
    }
    if (Object.keys(patch).length) await dbPatch(hat, patch);
    if (missing) await markListStale(hat);
  } catch {
    // Cosmetic and catch-up only; the next load tries again.
  }
}
